import React, { useState } from "react";
import { Button, Typography, Radio, IconButton, Select, MenuItem, Checkbox, Tabs, Tab } from "@mui/material";
import { tss } from "tss-react/mui";
import { Scrollbars } from "react-custom-scrollbars";
import { useStylesColorLegend } from "./ColorLegend";
import { SliderInput, ColorPicker, ColorBand, Section } from "components/";
//import HorizontalColorPalette from '../HorizontalColorPalette';
import RasterActions from "actions//RasterActions";
import { ColorGradient, LayerIcon } from "components";
import { ColorBands, BlendModes, ImageStretchTypes } from "constants/Raster";
import { hexToRgb, rgbToArray } from "services/UtilsService";
import { Close, MoveToInbox, AddCircle } from "@mui/icons-material";
import commonStyles, { backgroundColor, boxShadow } from "styles/common";

const useStyles = tss.create({
    root: {
        position: "absolute",
        top: 0,
        left: 300,
        display: "table",
        background: backgroundColor,
        boxShadow: boxShadow,
        borderLeft: "solid 1px #E6E6E6",
        width: 300,
        height: "fit-content",
    },
    layerTitle: {
        display: "inline-flex",
        position: "relative",
        left: 12,
        width: 190,
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        overflow: "hidden",
        alignItems: "center",
        color: "#002776",
        fontSize: 14,
        //fontWeight: "bold",
        letterSpacing: "0.1px",
        lineHeight: "16px",
    },
    button: {
        width: 16,
        height: 16,
        minWidth: 16,
        margin: "8px 4px 0 8px",
    },
    layerContainer: {
        display: "flex",
        height: 47,
        width: "100%",
        //backgroundColor: "rgb(235,238,244)",
    },
    toolIcon: {
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: 46,
        height: 46,
    },
    layerIcon: {
        width: 30,
        height: 30,
        borderRadius: "50%",
        position: "absolute",
        left: 7,
        top: 7,
    },
    icon: {
        marginTop: 2,
        marginLeft: 3,
    },
    slider: {
        height: 69,
    },
    row: {
        position: "relative",
        display: "inline-flex",
        alignItems: "center",
        height: 40,
        width: "100%",
    },
    shortRow: {
        position: "relative",
        display: "inline-flex",
        alignItems: "center",
        height: 30,
        width: "100%",
    },
    right: {
        position: "absolute",
        right: 0,
    },
    spectral: {
        height: 20,
    },
    paddLeft: {
        paddingLeft: 5,
        fontWeight: "bold",
    },
    label: {
        width: "100%",
        color: "#283C50",
        fontSize: 14,
        letterSpacing: "0px",
        lineHeight: "16px",
    },
    iconButton: {
        borderRadius: 4,
        width: 28,
        height: 28,
    },
    rgbMenuItem: {
        color: "#283C50",
        fontSize: 14,
        letterSpacing: "0px",
        lineHeight: "16px",
        display: "inline-flex",
        paddingLeft: 10,
        maxWidth: 154,
        overflow: "hidden",
    },
    numberInput: {
        color: "black",
        textAlign: "right",
        fontSize: 14,
        marginTop: 0,
    },
});

const nameMapping: any = {
    backgroundColor: "background_color",
    noDataColor: "no_data_color",
    colorGradientId: "color_raster_gradient",
};

const rasterSettings = (raster: any) => {
    return {
        properties: {
            visible: raster["transparency"] ? (raster["transparency"] === "1" ? true : false) : true,
            colorGradientId: raster[nameMapping["colorGradientId"]] ? raster[nameMapping["colorGradientId"]] : "color",
            transparency: {
                min: 0,
                max: 1,
                step: 0.01,
                value: raster["transparency"] ? parseFloat(raster["transparency"]) : 1.0,
            },
            grayscale: raster["grayscale"] ? (raster["grayscale"] === "1" ? true : false) : true,
            contrast: {
                min: 0,
                max: 1,
                step: 0.01,
                value: raster["contrast"] ? parseFloat(raster["contrast"]) : 0.5,
            },
            brightness: {
                min: 0,
                max: 1,
                step: 0.01,
                value: raster["brightness"] ? parseFloat(raster["brightness"]) : 0.5,
            },
            gamma: {
                min: 0.2,
                max: 3,
                step: 0.01,
                value: raster["gamma"] ? parseFloat(raster["gamma"]) : 1.0,
            },
        },
        blending: {
            transparent: raster["blend_transparent"] ? raster["blend_transparent"] === "1" : false,
            tolerance: {
                min: 0,
                max: 1,
                step: 0.01,
                value: raster["blend_selection_tolerance"] ? parseFloat(raster["blend_selection_tolerance"]) : 0.5,
            },
            transparentColor: raster["transparentColor"] ? raster["transparentColor"] : "#FFFFFF",
            outputColor: raster["outputColor"] ? raster["outputColor"] : "#FFA300",
            alphaOutput: {
                min: 0,
                max: 1,
                step: 0.01,
                value: raster["blend_output_transparency"] ? parseFloat(raster["blend_output_transparency"]) : 0.5,
            },
            blendMode: raster["blend_mode"] ? raster["blend_mode"] : 1,
            invertSelection: raster["blend_invert"] ? raster["blend_invert"] === "1" : false,
        },
        colorMapping: {
            bandMode: raster["band_mode"] ? parseInt(raster["band_mode"]) : 1,
            band: raster["band"] ? raster["band"] : "rgb",
            colorBands: {
                rgb: raster["rgb_bands"] ? raster["rgb_bands"] : "012",
                sentinal2A: "sentinal-rgb",
                aster: "aster-vegetation",
            },
            manualBands: {
                greyscale: 0,
                r: raster["rgb_bands"] ? parseInt(raster["rgb_bands"][0]) : 0,
                g: raster["rgb_bands"] ? parseInt(raster["rgb_bands"][1]) : 1,
                b: raster["rgb_bands"] ? parseInt(raster["rgb_bands"][2]) : 2,
            },
            manualBandsCheck: raster["rgb_manual_bands"] ? raster["rgb_manual_bands"] : "0111",
            lastPreset: raster["rgb_last_preset"] ? raster["rgb_last_preset"] : "012",
        },
        stretchImage: {
            stretchType: "no",
            stretchSize: {
                min: 0.1,
                max: 10,
                step: 0.1,
                value: raster["stretch_size"] ? parseFloat(raster["stretch_size"]) : 5.5,
            },
        },
    };
};

let globalSettings: any;

type IRasterProperties = {
    raster: any;
    layer: any;
    currentProject: any;
    handleRasterClose: () => void;
};

let transparencySlider: any;

function DisplayProperties(props: IRasterProperties) {
    const { classes } = useStyles();
    const { classes: common } = commonStyles();
    const { classes: colorStyles } = useStylesColorLegend();
    const [update, setUpdate] = useState(false);
    const [colorGradientMenu, setColorGradientMenu] = useState(false);
    let [needsApply, setNeedsApply] = useState(false);

    const { layer, raster } = props;
    const { colorGradientId, transparency, contrast, brightness, gamma } = globalSettings.properties;

    let opacity = raster.transparency ? parseFloat(raster.transparency) : 1.0;

    var onUpdate = (raster: any) => {
        //console.log('update.display', raster);
        if (transparencySlider) {
            let opacity = raster.transparency ? parseFloat(raster.transparency) : 1.0;
            transparencySlider.updateSlider(opacity);
        }
    };

    React.useEffect(() => {
        RasterActions.on("update", onUpdate);
        return () => {
            RasterActions.removeEventListener("update", onUpdate);
        };
    }, []);

    const handleChange = (event: any, name: string, value: any) => {
        globalSettings.properties[name].value = value;
        var propName = nameMapping[name] ? nameMapping[name] : name;

        if (propName === "transparency") {
            RasterActions.setTransparency(layer, raster, value);
        } else {
            raster[propName] = value + "";
            RasterActions.updateImageProperies(layer, raster, propName, value);
        }
    };

    function setColorGradient(gradientId: string) {
        globalSettings.properties["colorGradientId"] = gradientId;
        raster[nameMapping["colorGradientId"]] = gradientId;
        RasterActions.updateRaster(raster);
        setUpdate(!update);
        setColorGradientMenu(false);
    }

    function openColorGradientMenu() {
        setColorGradientMenu(true);
    }

    function applySettings() {
        setNeedsApply(false);
    }

    return (
        <Section name="displayProperties" label="Display Properties">
            <div className={common.sectionContent}>
                <div className={classes.row} style={{ display: "none" }}>
                    <Typography className={common.label}>Select Colour Ramp</Typography>
                    <div className={classes.right} style={{ display: "none" }}>
                        <IconButton
                            className={[common.button, classes.iconButton].join(" ")}
                            style={{ marginRight: 12 }}
                        >
                            <MoveToInbox className={common.shaded} />
                        </IconButton>
                        <IconButton className={[common.button, classes.iconButton].join(" ")}>
                            <AddCircle className={common.shaded} />
                        </IconButton>
                    </div>
                </div>
                <div style={{ display: "none" }}>
                    <ColorGradient
                        colorGradientId={colorGradientId}
                        setColorGradient={setColorGradient}
                        openMenu={colorGradientMenu}
                        {...props}
                    />
                </div>
                <div
                    className={colorStyles.sectionGradient}
                    onClick={openColorGradientMenu}
                    style={{ display: "none" }}
                >
                    {/** 
						<HorizontalColorPalette source={raster} />
						*/}
                </div>
                <div className={classes.spectral}></div>
                <SliderInput
                    ref={(elm: any) => (transparencySlider = elm)}
                    id={raster.ID}
                    name="transparency"
                    title="Opacity"
                    min={transparency.min}
                    max={transparency.max}
                    step={transparency.step}
                    value={opacity}
                    handleChange={handleChange}
                    className={classes.slider}
                />
                <SliderInput
                    id={raster.ID}
                    name="contrast"
                    title="Contrast"
                    min={contrast.min}
                    max={contrast.max}
                    step={contrast.step}
                    value={contrast.value}
                    handleChange={handleChange}
                    className={classes.slider}
                />
                <SliderInput
                    id={raster.ID}
                    name="brightness"
                    title="Brightness"
                    min={brightness.min}
                    max={brightness.max}
                    step={brightness.step}
                    value={brightness.value}
                    handleChange={handleChange}
                    className={classes.slider}
                />
                <SliderInput
                    id={raster.ID}
                    name="gamma"
                    title="Gamma"
                    min={gamma.min}
                    max={gamma.max}
                    step={gamma.step}
                    value={gamma.value}
                    handleChange={handleChange}
                    className={classes.slider}
                />
            </div>
            {needsApply ? (
                <div className={colorStyles.bottomButtonContainer}>
                    <Button
                        onClick={applySettings}
                        title="This panel contains changes that were not applied to the viewer. Click this button to update the view."
                    >
                        Apply
                    </Button>
                </div>
            ) : (
                ""
            )}
        </Section>
    );
}

function Blending(props: IRasterProperties) {
    const { classes } = useStyles();
    const { classes: common } = commonStyles();
    const { classes: colorStyles } = useStylesColorLegend();
    const { layer, raster } = props;
    const [update, setUpdate] = useState(false);
    const { transparent, tolerance, transparentColor, outputColor, blendMode, alphaOutput, invertSelection } =
        globalSettings.blending;

    const makeTransparent = (e: any) => {
        globalSettings.blending.transparent = e.target.checked;
        raster["blend_transparent"] = e.target.checked ? "1" : "0";
        RasterActions.updateRaster(raster);
        RasterActions.updateImageProperies(layer, raster, "blendMakeColorTransparent", e.target.checked);
        setUpdate(!update);
    };

    const handleToleranceChange = (event: any, name: string, value: any) => {
        globalSettings.blending[name].value = value;
        raster["blend_selection_tolerance"] = value + "";
        RasterActions.updateRaster(raster);

        RasterActions.updateImageProperies(layer, raster, "blendSelectionTolerance", value);
        setUpdate(!update);
    };

    const handleAlphaChange = (event: any, name: string, value: any) => {
        globalSettings.blending[name].value = value;
        raster["blend_output_transparency"] = value + "";
        RasterActions.updateRaster(raster);
        RasterActions.updateImageProperies(layer, raster, "blendOutputTransparency", value);
    };

    const handleColorChange = (name: string, color: any) => {
        var colorArray: any;
        if (typeof color === "string") {
            globalSettings.blending[name] = color;
            colorArray = rgbToArray(hexToRgb(color));
        } else {
            globalSettings.blending[name] = "rgba(" + color.r + "," + color.g + "," + color.b + "," + color.a + ")";
            colorArray = [color.r / 256, color.g / 256, color.b / 256, color.a];
        }

        var rasterName = nameMapping[name] ? nameMapping[name] : name;
        raster[rasterName] = color;
        RasterActions.updateRaster(raster);
        var actionName = "blendTransparentColor";
        if (name === "outputColor") {
            actionName = "blendOutputColor";
            colorArray = [...colorArray, 1.0];
        }
        RasterActions.updateImageProperies(layer, raster, actionName, colorArray);
    };

    const handleBlendMode = (evt: any) => {
        var value = evt.target.value;
        globalSettings.blending["blendMode"] = value;
        raster["blend_mode"] = value + "";
        RasterActions.updateRaster(raster);
        RasterActions.updateImageProperies(layer, raster, "blendMode", value);
        setUpdate(!update);
    };

    const handleInvertSelection = (e: any) => {
        globalSettings.blending.invertSelection = e.target.checked;
        raster["blend_invert"] = e.target.checked ? "1" : "0";
        RasterActions.updateRaster(raster);
        RasterActions.updateImageProperies(layer, raster, "blendInvertSelection", e.target.checked);
        setUpdate(!update);
    };

    return (
        <Section name="blending" label="Blending">
            <div className={common.sectionContent}>
                <div className={colorStyles.subSectionContainer}>
                    <div className={classes.shortRow}>
                        <Typography className={classes.label}>Blend Mode</Typography>
                    </div>
                    <div className={classes.row}>
                        <Select className={classes.label} value={blendMode} onChange={handleBlendMode}>
                            {BlendModes.map((mode: any) => {
                                return (
                                    <MenuItem
                                        key={"blend-mode-" + mode.value}
                                        className={classes.label}
                                        value={mode.value}
                                    >
                                        {mode.label}
                                    </MenuItem>
                                );
                            })}
                        </Select>
                    </div>
                    <div
                        className={classes.row}
                        style={{
                            marginLeft: -12,
                            width: 281,
                            marginTop: 12,
                        }}
                    >
                        <Checkbox checked={transparent} onChange={(e) => makeTransparent(e)} />
                        <Typography className={common.label}>Replace Color</Typography>
                    </div>
                    <div style={{ display: !transparent ? "none" : "block" }}>
                        <div className={classes.row} style={{ marginBottom: 12 }}>
                            <Typography className={common.label}>Color To Replace</Typography>
                            <div className={classes.right}>
                                <ColorPicker
                                    id={raster.ID}
                                    name="transparentColor"
                                    defaultColor={transparentColor}
                                    handleColorChange={handleColorChange}
                                />
                            </div>
                        </div>
                        <div className={classes.row} style={{ marginBottom: 12 }}>
                            <Typography className={common.label}>Output Color</Typography>
                            <div className={classes.right}>
                                <ColorPicker
                                    id={raster.ID}
                                    name="outputColor"
                                    defaultColor={outputColor}
                                    handleColorChange={handleColorChange}
                                />
                            </div>
                        </div>
                        <SliderInput
                            id={raster.ID}
                            name="alphaOutput"
                            title="Output Color Opacity"
                            min={alphaOutput.min}
                            max={alphaOutput.max}
                            step={alphaOutput.step}
                            value={alphaOutput.value}
                            handleChange={handleAlphaChange}
                            className={classes.slider}
                            style={{ marginTop: 10 }}
                        />
                        <SliderInput
                            id={raster.ID}
                            name="tolerance"
                            title="Selection Tolerance"
                            min={tolerance.min}
                            max={tolerance.max}
                            step={tolerance.step}
                            value={tolerance.value}
                            handleChange={handleToleranceChange}
                            className={classes.slider}
                        />
                        <div
                            className={classes.row}
                            style={{
                                marginLeft: -9,
                                width: 281,
                            }}
                        >
                            <Checkbox checked={invertSelection} onChange={(e) => handleInvertSelection(e)} />
                            <Typography className={common.label}>Invert Selection</Typography>
                        </div>
                    </div>
                </div>
            </div>
        </Section>
    );
}

function ColourMapping(props: IRasterProperties) {
    const { classes } = useStyles();
    const { classes: common } = commonStyles();
    const { classes: colorStyles } = useStylesColorLegend();
    const { raster, layer } = props;
    const [update, setUpdate] = useState(false);
    var { bandMode, band, colorBands, manualBands, manualBandsCheck } = globalSettings.colorMapping;

    //console.log('globalSettings.colorMapping:', globalSettings.colorMapping);
    const rasterBands = raster["raster_bands"] ? raster["bands"].split(",") : ["r", "g", "b"];
    const rasterBandsLength = rasterBands.length;

    const setPreset = () => {
        globalSettings.colorMapping.bandMode = 1;
        raster["band_mode"] = 1;
        applySettings();
    };

    const setManual = () => {
        globalSettings.colorMapping.bandMode = 2;
        raster["band_mode"] = 2;
        applySettings();
    };

    const handleChange = (evt: any) => {
        var value = evt.target.value;
        globalSettings.colorMapping.bands = value;
        applySettings();
    };

    const handleColorBandChange = (evt: any, band: string) => {
        var newBand: any = { ...colorBands };
        var value = evt.target.value;
        newBand[band] = value;
        globalSettings.colorMapping.colorBands[band] = value;
        raster["rgb_last_preset"] = value;
        applySettings();
    };

    const handleManualBandChange = (evt: any, rgbBand: string) => {
        var newBand: any = { ...manualBands };
        var value = evt.target.value;
        var apply = value !== -1;
        if (apply) {
            newBand[rgbBand] = value;
            if (rgbBand === "greyscale" && manualBandsCheck[0] === "0" && value !== -1) {
                newBand["r"] = value;
                newBand["g"] = value;
                newBand["b"] = value;
            }
            globalSettings.colorMapping.manualBands = newBand;
            applySettings();
        }
    };

    const handleManualBandcheckValue = (evt: any, band: string) => {
        var checked = Boolean(evt.target.checked);
        var newValue: any = { ...manualBandsCheck };
        newValue[band] = checked;
        if (band === "greyscale") {
            if (checked) {
                newValue = "1000";
            } else {
                newValue = "0111";
            }
        }
        if (band !== "greyscale") {
            newValue = "0111";
        }
        globalSettings.colorMapping.manualBandsCheck = newValue;
        raster["rgb_manual_bands"] = newValue;
        applySettings();
    };

    const applySettings = () => {
        const { bandMode, colorBands, manualBands, manualBandsCheck, lastPreset } = globalSettings.colorMapping;
        if (bandMode === 1) {
            if (colorBands[band] === "000" || colorBands[band] === "111" || colorBands[band] === "222") {
                globalSettings.colorMapping.colorBands[band] = lastPreset;
                raster[band + "_bands"] = lastPreset;
            } else {
                raster[band + "_bands"] = colorBands[band];
            }
        } else {
            if (manualBandsCheck[0] === "1") {
                if (manualBands.greyscale !== -1) {
                    raster[band + "_bands"] =
                        manualBands.greyscale + "" + manualBands.greyscale + "" + manualBands.greyscale;
                }
            } else {
                raster[band + "_bands"] = manualBands.r + "" + manualBands.g + "" + manualBands.b;
            }
        }
        var value = raster[band + "_bands"];
        RasterActions.updateImageProperies(layer, raster, "bands", [
            parseInt(value[0]),
            parseInt(value[1]),
            parseInt(value[2]),
            3,
        ]);
        RasterActions.updateRaster(raster);
        setUpdate(!update);
    };
    const handleTabChange = (e: any, newValue: number) => {
        if (newValue === 1) {
            setPreset();
        } else {
            setManual();
        }
    };
    return (
        <Section name="colourMapping" label="Colour Mapping (RGB Bands)">
            <div className={common.sectionContent}>
                <div className={colorStyles.modeSelector}>
                    <Tabs
                        value={bandMode}
                        onChange={handleTabChange}
                        indicatorColor="primary"
                        textColor="primary"
                        className={common.tabs}
                    >
                        <Tab value={1} label="Static Colour" className={common.tab} />
                        <Tab value={2} label="Data-Driven Colour" className={common.tab} />
                    </Tabs>
                </div>
                <div
                    className={colorStyles.subSectionContainer}
                    style={bandMode === 1 ? { display: "block" } : { display: "none" }}
                >
                    <Typography className={colorStyles.subSectionLabel}>Select Pre-set Bands</Typography>
                    <div className={classes.row}>
                        <Select className={classes.label} style={{ width: 270 }} value={band} onChange={handleChange}>
                            {Object.values(ColorBands).map((entry: any) => {
                                return (
                                    <MenuItem
                                        key={"colorBand" + entry.value}
                                        className={classes.label}
                                        value={entry.value}
                                        style={{
                                            display: rasterBandsLength >= entry.bands ? "block" : "none",
                                        }}
                                    >
                                        {entry.label}
                                    </MenuItem>
                                );
                            })}
                        </Select>
                    </div>
                    <div className={classes.row}>
                        <Typography className={colorStyles.subSectionLabel}>Select Pre-set RGB Bands</Typography>
                    </div>
                    <div className={classes.row} style={{ display: band === "rgb" ? "block" : "none" }}>
                        <Select
                            value={colorBands.rgb}
                            style={{ width: "100%" }}
                            onChange={(e: any) => handleColorBandChange(e, "rgb")}
                        >
                            {ColorBands["rgb"].values.map((entry) => {
                                return (
                                    <MenuItem key={entry.value} className={classes.label} value={entry.value}>
                                        <ColorBand width={26} background="#FF0000" label={entry.r} />
                                        <ColorBand width={26} background="#00E600" label={entry.g} />
                                        <ColorBand width={26} background="#0000FF" label={entry.b} />
                                        <Typography className={classes.rgbMenuItem}>{entry.label}</Typography>
                                    </MenuItem>
                                );
                            })}
                        </Select>
                    </div>
                    <div className={classes.row} style={{ display: band === "sentinal2A" ? "block" : "none" }}>
                        <Select
                            value={colorBands.sentinal2A}
                            style={{ width: "100%" }}
                            onChange={(e: any) => handleColorBandChange(e, "sentinal")}
                        >
                            {ColorBands["sentinal2A"].values.map((entry) => {
                                return (
                                    <MenuItem key={entry.value} className={classes.label} value={entry.value}>
                                        <ColorBand width={26} background="#FF0000" label={entry.r} />
                                        <ColorBand width={26} background="#00E600" label={entry.g} />
                                        <ColorBand width={26} background="#0000FF" label={entry.b} />
                                        <Typography className={classes.rgbMenuItem}>{entry.label}</Typography>
                                    </MenuItem>
                                );
                            })}
                        </Select>
                    </div>
                    <div className={classes.row} style={{ display: band === "aster" ? "block" : "none" }}>
                        <Select
                            value={colorBands.aster}
                            style={{ width: "100%" }}
                            onChange={(e: any) => handleColorBandChange(e, "aster")}
                        >
                            {ColorBands["aster"].values.map((entry) => {
                                return (
                                    <MenuItem key={entry.value} className={classes.label} value={entry.value}>
                                        <ColorBand width={26} background="#FF0000" label={entry.r} />
                                        <ColorBand width={26} background="#00E600" label={entry.g} />
                                        <ColorBand width={26} background="#0000FF" label={entry.b} />
                                        <Typography className={classes.rgbMenuItem}>{entry.label}</Typography>
                                    </MenuItem>
                                );
                            })}
                        </Select>
                    </div>
                </div>
                <div
                    className={colorStyles.subSectionContainer}
                    style={bandMode === 2 ? { display: "block" } : { display: "none" }}
                >
                    <Typography className={colorStyles.subSectionLabel}>Assign Rasters Bands</Typography>
                    <div className={classes.row} style={{ marginLeft: -12, width: 281 }}>
                        <Checkbox
                            checked={manualBandsCheck[0] === "1"}
                            onChange={(e) => handleManualBandcheckValue(e, "greyscale")}
                        />
                        <ColorBand width={30} background="#A9B1B9" />
                        <Typography className={[common.label, classes.paddLeft].join(" ")}> Greyscale</Typography>
                        <div className={classes.right}>
                            <Select
                                className={classes.label}
                                value={manualBands.greyscale}
                                onChange={(e) => handleManualBandChange(e, "greyscale")}
                                style={{ width: 114 }}
                            >
                                <MenuItem className={classes.label} value={-1} disabled>
                                    <em>Assign Band</em>
                                </MenuItem>
                                {rasterBands.map((band: string, index: number) => {
                                    return (
                                        <MenuItem key={"greyscale-" + band} className={classes.label} value={index}>
                                            {band.toLocaleUpperCase()}
                                        </MenuItem>
                                    );
                                })}
                            </Select>
                        </div>
                    </div>
                    <div className={classes.row} style={{ marginLeft: -12, width: 281 }}>
                        <Checkbox
                            checked={manualBandsCheck[1] === "1"}
                            onChange={(e) => handleManualBandcheckValue(e, "r")}
                        />
                        <ColorBand width={30} background="#FF0000" />{" "}
                        <Typography className={[common.label, classes.paddLeft].join(" ")}> Red</Typography>
                        <div className={classes.right}>
                            <Select
                                className={classes.label}
                                value={manualBands.r}
                                onChange={(e) => handleManualBandChange(e, "r")}
                                style={{ width: 114 }}
                            >
                                <MenuItem className={classes.label} disabled>
                                    <em>Assign Band</em>
                                </MenuItem>
                                {rasterBands.map((band: string, index: number) => {
                                    return (
                                        <MenuItem key={"r-" + band} className={classes.label} value={index}>
                                            {band.toLocaleUpperCase()}
                                        </MenuItem>
                                    );
                                })}
                            </Select>
                        </div>
                    </div>
                    <div className={classes.row} style={{ marginLeft: -12, width: 281 }}>
                        <Checkbox
                            checked={manualBandsCheck[2] === "1"}
                            onChange={(e) => handleManualBandcheckValue(e, "g")}
                        />
                        <ColorBand width={30} background="#00E600" />{" "}
                        <Typography className={[common.label, classes.paddLeft].join(" ")}> Green</Typography>
                        <div className={classes.right}>
                            <Select
                                className={classes.label}
                                value={manualBands.g}
                                onChange={(e) => handleManualBandChange(e, "g")}
                                style={{ width: 114 }}
                            >
                                <MenuItem className={classes.label} disabled>
                                    <em>Assign Band</em>
                                </MenuItem>
                                {rasterBands.map((band: string, index: number) => {
                                    return (
                                        <MenuItem key={"g-" + band} className={classes.label} value={index}>
                                            {band.toLocaleUpperCase()}
                                        </MenuItem>
                                    );
                                })}
                            </Select>
                        </div>
                    </div>
                    <div className={classes.row} style={{ marginLeft: -12, width: 281 }}>
                        <Checkbox
                            checked={manualBandsCheck[3] === "1"}
                            onChange={(e) => handleManualBandcheckValue(e, "b")}
                        />
                        <ColorBand width={30} background="#0000FF" />
                        <Typography className={[common.label, classes.paddLeft].join(" ")}> Blue</Typography>
                        <div className={classes.right}>
                            <Select
                                className={classes.label}
                                value={manualBands.b}
                                onChange={(e) => handleManualBandChange(e, "b")}
                                style={{ width: 114 }}
                            >
                                <MenuItem className={classes.label} disabled>
                                    <em>Assign Band</em>
                                </MenuItem>
                                {rasterBands.map((band: string, index: number) => {
                                    return (
                                        <MenuItem key={"b-" + band} className={classes.label} value={index}>
                                            {band.toLocaleUpperCase()}
                                        </MenuItem>
                                    );
                                })}
                            </Select>
                        </div>
                    </div>
                </div>
            </div>
        </Section>
    );
}

function StretchImage(props: IRasterProperties) {
    const { classes } = useStyles();
    const { classes: common } = commonStyles();
    const { raster } = props;
    const [band, setBand] = useState(0);
    const [stretchType, setStretchType] = useState("no");
    const { stretchSize } = globalSettings.stretchImage;

    const handleChange = (evt: any) => {
        setBand(parseInt(evt.target.value));
    };

    const handleStretch = (evt: any) => {
        setStretchType(evt.target.value);
    };

    const handleInputChange = (evt: any) => {
        globalSettings.stretchImage.stretchSize.value = parseFloat(evt.target.value);
    };

    return (
        <Section name="stretchImage" label="Stretch Image" style={{ display: "none" }}>
            <div className={common.sectionContent}>
                <Typography className={common.label}>Select RGB Bands to stretch</Typography>
                <div className={classes.row} style={{ marginTop: 10 }}>
                    <div className={classes.row} style={{ marginLeft: -12 }}>
                        <Radio checked={band === 0} onChange={handleChange} value={0} name="radio-button-demo" />
                        <ColorBand background="#FF0000" label="R" />
                        <ColorBand background="#00E600" label="G" />
                        <ColorBand background="#0000FF" label="B" />
                    </div>
                    <div className={classes.row}>
                        <Radio checked={band === 1} onChange={handleChange} value={1} name="radio-button-demo" />
                        <ColorBand background="#FF0000" label="R" />
                    </div>
                    <div className={classes.row}>
                        <Radio checked={band === 2} onChange={handleChange} value={2} name="radio-button-demo" />
                        <ColorBand background="#00E600" label="G" />
                    </div>
                    <div className={classes.row}>
                        <Radio checked={band === 3} onChange={handleChange} value={3} name="radio-button-demo" />
                        <ColorBand background="#0000FF" label="B" />
                    </div>
                </div>
                <div className={classes.row}>
                    <Typography className={classes.label}>Stretch Type</Typography>
                </div>
                <div className={classes.row}>
                    <Select className={classes.label} value={stretchType} onChange={handleStretch}>
                        {ImageStretchTypes.map((stretchType: any) => {
                            return (
                                <MenuItem
                                    key={"stretech-type-" + stretchType.value}
                                    className={classes.label}
                                    value={stretchType.value}
                                >
                                    {stretchType.label}
                                </MenuItem>
                            );
                        })}
                    </Select>
                </div>
                {stretchType === "percent" ? (
                    <SliderInput
                        id={raster.ID}
                        name="stretchSize"
                        title="Stretch Size"
                        min={stretchSize.min}
                        max={stretchSize.max}
                        step={stretchSize.step}
                        value={stretchSize.value}
                        handleChange={handleInputChange}
                        className={classes.slider}
                        style={{ marginTop: 15 }}
                    />
                ) : (
                    ""
                )}
            </div>
        </Section>
    );
}

type IRasterProps = {
    currentProject: any;
    layer: any;
    raster: any;
    inputs: any;
    handleRasterClose: () => void;
};

export default function RasterProperties(props: IRasterProps) {
    const { classes } = useStyles();
    const { classes: common } = commonStyles();
    const { raster, inputs, handleRasterClose } = props;
    globalSettings = rasterSettings(raster);
    RasterActions.imageProperties[raster.ID] = {
        ...RasterActions.imageProperties[raster.ID],
        transparency: globalSettings.properties.transparency.value,
        //TODO GRAYSCALE???
        contrast: globalSettings.properties.contrast.value,
        brightness: globalSettings.properties.brightness.value,
        gamma: globalSettings.properties.gamma.value,
        //TODO BLEND PROPERTIES???
        bands: [0, 1, 2, 3],
    };
    return (
        <div className={classes.root}>
            <div className={classes.layerContainer}>
                <LayerIcon layer={raster} inputs={inputs} />{" "}
                <Typography className={classes.layerTitle}> {raster.name}</Typography>
                <div style={{ position: "absolute", right: 2, top: 7 }}>
                    <Button className={classes.button} onClick={handleRasterClose}>
                        <Close className={common.closeButton} />
                    </Button>
                </div>
            </div>
            <Scrollbars
                autoHide
                autoHideTimeout={1000}
                autoHeight
                autoHeightMax={"calc(100vh - 132px)"}
                style={{ width: 300, overflowX: "hidden" }}
            >
                <DisplayProperties {...props} />
                <ColourMapping {...props} />
                <Blending {...props} />
                <StretchImage {...props} />
            </Scrollbars>
        </div>
    );
}
